import React, { useState, useRef, useEffect } from "react";
import { Box, IconButton, Modal, Button } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import ZoomInIcon from "@mui/icons-material/ZoomIn";
import ZoomOutIcon from "@mui/icons-material/ZoomOut";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";

const SWIPE_THRESHOLD = 50;

// `images`/`activeIndex` drive the zoomed view so it can be swiped/arrowed through; a caller with
// only a single image can still pass `src` and navigation simply stays disabled.
const DesktopProductImage = ({ src, alt, images, activeIndex = 0, onActiveIndexChange }) => {
  const galleryImages = images && images.length > 0 ? images : [src];
  const canNavigate = galleryImages.length > 1;

  const [zoomOpen, setZoomOpen] = useState(false);
  const [zoomIndex, setZoomIndex] = useState(activeIndex);
  const [zoomLevel, setZoomLevel] = useState(1);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const imageRef = useRef(null);
  const containerRef = useRef(null);
  const swipeStartRef = useRef(null);

  useEffect(
    () => {
      const container = containerRef.current;
      if (!container) return;

      const handleWheel = (e) => {
        // Only preventDefault when we actually want to zoom
        // (you can also check zoomLevel > 1 or any other condition)
        e.preventDefault();

        if (e.deltaY < 0) {
          setZoomLevel((prev) => Math.min(prev + 0.1, 3));
        } else {
          setZoomLevel((prev) => Math.max(prev - 0.1, 1));
        }
      };

      // Important: { passive: false }
      container.addEventListener("wheel", handleWheel, { passive: false });

      // Cleanup!
      return () => {
        container.removeEventListener("wheel", handleWheel, { passive: false });
      };
    },
    [
      /* dependencies — add setZoomLevel deps if you extract handler outside */
    ]
  );

  const handleZoomIn = () => {
    setZoomLevel((prev) => Math.min(prev + 0.5, 3));
  };

  const handleZoomOut = () => {
    setZoomLevel((prev) => Math.max(prev - 0.5, 1));
  };

  const handleResetZoom = () => {
    setZoomLevel(1);
    setPosition({ x: 0, y: 0 });
  };

  const openZoom = () => {
    setZoomIndex(activeIndex);
    handleResetZoom();
    setZoomOpen(true);
  };

  const closeZoom = () => {
    setZoomOpen(false);
    handleResetZoom();
    onActiveIndexChange?.(zoomIndex);
  };

  const goToPrevImage = () => {
    if (!canNavigate) return;
    handleResetZoom();
    setZoomIndex((prev) => (prev === 0 ? galleryImages.length - 1 : prev - 1));
  };

  const goToNextImage = () => {
    if (!canNavigate) return;
    handleResetZoom();
    setZoomIndex((prev) => (prev === galleryImages.length - 1 ? 0 : prev + 1));
  };

  const handleMouseDown = (e) => {
    if (zoomLevel > 1) {
      setIsDragging(true);
      setDragStart({
        x: e.clientX - position.x,
        y: e.clientY - position.y,
      });
    }
  };

  const handleMouseMove = (e) => {
    if (isDragging && zoomLevel > 1) {
      const newX = e.clientX - dragStart.x;
      const newY = e.clientY - dragStart.y;

      // Calculate boundaries based on zoom level
      const maxX = (imageRef.current?.offsetWidth * (zoomLevel - 1)) / 2;
      const maxY = (imageRef.current?.offsetHeight * (zoomLevel - 1)) / 2;

      setPosition({
        x: Math.max(Math.min(newX, maxX), -maxX),
        y: Math.max(Math.min(newY, maxY), -maxY),
      });
    }
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  const handleTouchStart = (e) => {
    if (zoomLevel > 1 && e.touches.length === 1) {
      setIsDragging(true);
      setDragStart({
        x: e.touches[0].clientX - position.x,
        y: e.touches[0].clientY - position.y,
      });
    } else if (e.touches.length === 2) {
      // Handle pinch zoom for touch devices
      e.preventDefault();
    } else if (e.touches.length === 1) {
      // Not zoomed in: a single-finger drag is a swipe-to-navigate gesture instead of a pan.
      swipeStartRef.current = { x: e.touches[0].clientX, y: e.touches[0].clientY };
    }
  };

  const handleTouchMove = (e) => {
    if (isDragging && zoomLevel > 1 && e.touches.length === 1) {
      const newX = e.touches[0].clientX - dragStart.x;
      const newY = e.touches[0].clientY - dragStart.y;

      // Calculate boundaries
      if (imageRef.current) {
        const imgWidth = imageRef.current.offsetWidth;
        const imgHeight = imageRef.current.offsetHeight;
        const maxX = (imgWidth * (zoomLevel - 1)) / 2;
        const maxY = (imgHeight * (zoomLevel - 1)) / 2;

        setPosition({
          x: Math.max(Math.min(newX, maxX), -maxX),
          y: Math.max(Math.min(newY, maxY), -maxY),
        });
      }
    }
  };

  const handleTouchEnd = (e) => {
    setIsDragging(false);

    const swipeStart = swipeStartRef.current;
    swipeStartRef.current = null;

    if (zoomLevel === 1 && swipeStart && e.changedTouches?.length === 1) {
      const deltaX = e.changedTouches[0].clientX - swipeStart.x;
      const deltaY = e.changedTouches[0].clientY - swipeStart.y;

      if (Math.abs(deltaX) > SWIPE_THRESHOLD && Math.abs(deltaX) > Math.abs(deltaY)) {
        if (deltaX > 0) {
          goToPrevImage();
        } else {
          goToNextImage();
        }
      }
    }
  };

  // Rebinds whenever the modal opens/closes or the viewed image changes, so Escape/Arrow keys
  // always act on the latest zoomIndex rather than a stale closure from when the modal opened.
  useEffect(() => {
    if (!zoomOpen) return;

    const handleKeyDown = (e) => {
      if (e.key === "Escape") {
        closeZoom();
      } else if (e.key === "ArrowLeft") {
        goToPrevImage();
      } else if (e.key === "ArrowRight") {
        goToNextImage();
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [zoomOpen, zoomIndex]);

  return (
    <>
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          width: "100%",
          height: "400px",
          position: "relative",
          cursor: "zoom-in",
        }}
        onClick={openZoom}
      >
        <img
          src={galleryImages[activeIndex] ?? src}
          alt={alt}
          style={{
            maxWidth: "100%",
            maxHeight: "100%",
            objectFit: "contain",
            display: "block",
          }}
        />
        <Box
          sx={{
            position: "absolute",
            bottom: 8,
            right: 8,
            backgroundColor: "rgba(0, 0, 0, 0.7)",
            borderRadius: "50%",
            padding: "4px",
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <ZoomInIcon sx={{ color: "white", fontSize: "20px" }} />
        </Box>
      </Box>

      {/* Zoom Modal */}
      <Modal
        open={zoomOpen}
        onClose={closeZoom}
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          backdropFilter: "blur(4px)",
        }}
        BackdropProps={{
          style: {
            backgroundColor: "rgba(0, 0, 0, 0.7)",
          },
          onClick: (e) => {
            // Prevent closing when clicking on backdrop
            e.stopPropagation();
          },
        }}
        disableEscapeKeyDown={false} // Allow ESC key to work
      >
        <Box
          ref={containerRef}
          sx={{
            position: "relative",
            width: "90vw",
            height: "90vh",
            overflow: "hidden",
            cursor:
              zoomLevel > 1 ? (isDragging ? "grabbing" : "grab") : "default",
          }}
          onMouseDown={handleMouseDown}
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUp}
          onMouseLeave={handleMouseUp}
          onWheel={(e) => {
            // e.preventDefault();
            if (e.deltaY < 0) {
              // Zoom in
              setZoomLevel((prev) => Math.min(prev + 0.1, 3));
            } else {
              // Zoom out
              setZoomLevel((prev) => Math.max(prev - 0.1, 1));
            }
          }}
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
        >
          {/* Close Button */}
          <IconButton
            onClick={closeZoom}
            sx={{
              position: "absolute",
              top: 16,
              right: 16,
              zIndex: 1000,
              backgroundColor: "rgba(0, 0, 0, 0.5)",
              color: "white",
              "&:hover": {
                backgroundColor: "rgba(0, 0, 0, 0.8)",
              },
            }}
          >
            <CloseIcon />
          </IconButton>

          {/* Previous / Next Controls */}
          {canNavigate && (
            <>
              <IconButton
                onClick={goToPrevImage}
                sx={{
                  position: "absolute",
                  top: "50%",
                  left: 16,
                  transform: "translateY(-50%)",
                  zIndex: 1000,
                  backgroundColor: "rgba(0, 0, 0, 0.5)",
                  color: "white",
                  "&:hover": {
                    backgroundColor: "rgba(0, 0, 0, 0.8)",
                  },
                }}
              >
                <ChevronLeftIcon />
              </IconButton>
              <IconButton
                onClick={goToNextImage}
                sx={{
                  position: "absolute",
                  top: "50%",
                  right: 16,
                  transform: "translateY(-50%)",
                  zIndex: 1000,
                  backgroundColor: "rgba(0, 0, 0, 0.5)",
                  color: "white",
                  "&:hover": {
                    backgroundColor: "rgba(0, 0, 0, 0.8)",
                  },
                }}
              >
                <ChevronRightIcon />
              </IconButton>

              {/* Image Counter */}
              <Box
                sx={{
                  position: "absolute",
                  top: 16,
                  left: "50%",
                  transform: "translateX(-50%)",
                  zIndex: 1000,
                  backgroundColor: "rgba(0, 0, 0, 0.5)",
                  color: "white",
                  padding: "4px 12px",
                  borderRadius: "4px",
                  fontSize: "14px",
                }}
              >
                {zoomIndex + 1} / {galleryImages.length}
              </Box>
            </>
          )}

          {/* Zoom Controls */}
          <Box
            sx={{
              position: "absolute",
              top: 16,
              left: 16,
              zIndex: 1000,
              display: "flex",
              gap: 1,
              backgroundColor: "rgba(0, 0, 0, 0.5)",
              borderRadius: "4px",
              padding: "4px",
            }}
          >
            <IconButton
              onClick={handleZoomIn}
              sx={{ color: "white" }}
              disabled={zoomLevel >= 3}
            >
              <ZoomInIcon />
            </IconButton>
            <IconButton
              onClick={handleZoomOut}
              sx={{ color: "white" }}
              disabled={zoomLevel <= 1}
            >
              <ZoomOutIcon />
            </IconButton>
            <Button
              onClick={handleResetZoom}
              sx={{
                color: "white",
                minWidth: "auto",
                padding: "6px 12px",
                fontSize: "12px",
              }}
            >
              Reset
            </Button>
          </Box>

          {/* Zoom Level Indicator */}
          <Box
            sx={{
              position: "absolute",
              bottom: "10%",
              left: "50%",
              transform: "translateX(-50%)",
              zIndex: 1000,
              backgroundColor: "rgba(0, 0, 0, 0.5)",
              color: "white",
              padding: "4px 12px",
              borderRadius: "4px",
              fontSize: "14px",
            }}
          >
            {Math.round(zoomLevel * 100)}%
          </Box>

          {/* Zoomed Image Container */}
          <Box
            ref={imageRef}
            sx={{
              width: "100%",
              height: "100%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              transform: `scale(${zoomLevel}) translate(${position.x}px, ${position.y}px)`,
              transition: isDragging ? "none" : "transform 0.1s ease",
              willChange: "transform",
            }}
          >
            <img
              src={galleryImages[zoomIndex]}
              alt={alt}
              style={{
                maxHeight: "100%",
                maxWidth: "100%",
                objectFit: "contain",
                userSelect: "none",
                pointerEvents: "none", // Prevent image from interfering with drag events
              }}
              draggable="false" // Prevent native drag behavior
            />
          </Box>

          {/* Instructions */}
          <Box
            sx={{
              position: "absolute",
              bottom: 16,
              left: "50%",
              transform: "translateX(-50%)",
              zIndex: 1000,
              backgroundColor: "rgba(0, 0, 0, 0.5)",
              color: "white",
              padding: "8px 16px",
              borderRadius: "4px",
              fontSize: "14px",
              textAlign: "center",
              display: { xs: "none", sm: "block" }, // Hide on mobile to save space
            }}
          >
            {zoomLevel > 1
              ? "Drag to pan • Mouse wheel to zoom • ESC or X to close"
              : canNavigate
              ? "Swipe or arrows to browse • Mouse wheel to zoom • ESC or X to close"
              : "Mouse wheel or buttons to zoom • ESC or X to close"}
          </Box>
        </Box>
      </Modal>
    </>
  );
};

export default DesktopProductImage;
